/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { isStrEmptyOrNull } from '../../common/UtilityFunctions';
import DefaultModal from './DefaultModal';
import { Button, Divider } from '@mui/material';
import Styles from '../../css/ServicerEscrowAnalysis.module.css';
import { useApplyAEAContext } from '../../contexts/ApplyAEAContext';
import {
  getEffectiveDateUTCTimestamp,
  useEscrowAnalysisCalculationContext,
} from '../../contexts/CalculateEscrowAnalysisContext';
import { useCallback } from 'react';
import { useLoanContext } from '../../contexts/LoanContext';

export function getRefundNotApplicableStr(refundNotApplicableStr: string) {
  const strArr = refundNotApplicableStr.split('.');
  let refundStr = '';
  strArr.forEach((element) => {
    if (!isStrEmptyOrNull(element)) {
      refundStr = refundStr + '\u2022 ' + element + '\n';
    }
  });
  return refundStr;
}

const confirmApplyAEAModal = () => {
  const { setApplyAEAClicked, applyAEAClicked, applyingAEA, applyAEA, setApplyAEAResultRetrieved } =
    useApplyAEAContext();

  const { escrowAnalysisCalculation, shortageMonths, effectiveDate, effdatestr, escrowCushionMonths } =
    useEscrowAnalysisCalculationContext();

  const { loanNumber } = useLoanContext();

  const handleApply = useCallback(async () => {
    setApplyAEAClicked(false);
    const params: ApplyAEAParams = {
      escrowShortSurplus: escrowAnalysisCalculation.escrowShortSurplus,
      newEscrowPayment: escrowAnalysisCalculation.newEscrowPayment,
      currentBalance: escrowAnalysisCalculation.currentBalance,
      projectedBalance: escrowAnalysisCalculation.projectedBalance,
      estimatedBalance: escrowAnalysisCalculation.estimatedBalance,
      newMonthlyPayment: escrowAnalysisCalculation.newMonthlyPayment,
      paymentChange: escrowAnalysisCalculation.paymentChange,
      escrowShortageRepayMonths: shortageMonths,
      lastPaidInstallmentDate: escrowAnalysisCalculation.lastPaidInstallmentDate,
      effectiveDateUTCTimestamp: getEffectiveDateUTCTimestamp(effectiveDate),
      dateToCheck: effdatestr,
      applyRefund:
        escrowAnalysisCalculation.intendedRefund > 0 && isStrEmptyOrNull(escrowAnalysisCalculation.refundNotApplicable),
      trialBalances: escrowAnalysisCalculation.trialBalances,
      escrowAnalysisTiCushion: escrowCushionMonths,
      newMonthlyImpound: escrowAnalysisCalculation.newMonthlyImpound,
      refundNotApplicable: escrowAnalysisCalculation.refundNotApplicable,
      intendedRefund: escrowAnalysisCalculation.intendedRefund,
      expectedMonthlyPayment: escrowAnalysisCalculation.expectedMonthlyPayment,
      expectedEscrowPayment: escrowAnalysisCalculation.expectedEscrowPayment,
      pitiDiff: escrowAnalysisCalculation.pitiDiff,
    };
    await applyAEA(params);
    setApplyAEAResultRetrieved(true);
  }, [loanNumber, effectiveDate, escrowAnalysisCalculation, effdatestr, applyAEA, shortageMonths, escrowCushionMonths]);

  return (
    <DefaultModal
      open={applyAEAClicked}
      title={<div className={Styles.modaltitle}>Apply AEA?</div>}
      content={
        <div className={Styles.modalcontainer}>
          <div className={Styles.row}>
            <div>
              <p className={Styles.modallabel}>New Escrow Payment:</p>
              <p className={Styles.modallabel}>New Monthly Payment:</p>
              <p className={Styles.modallabel}>Payment Change:</p>
              {escrowAnalysisCalculation.intendedRefund > 0 &&
                isStrEmptyOrNull(escrowAnalysisCalculation.refundNotApplicable) && (
                  <p className={Styles.modallabel}>Refund:</p>
                )}
            </div>
            <div>
              <p>${parseFloat(escrowAnalysisCalculation.newMonthlyImpound).toFixed(2)}</p>
              <p>${parseFloat(escrowAnalysisCalculation.newMonthlyPayment).toFixed(2)}</p>
              <p>${parseFloat(escrowAnalysisCalculation.pitiDiff).toFixed(2)}</p>
              {escrowAnalysisCalculation.intendedRefund > 0 &&
                isStrEmptyOrNull(escrowAnalysisCalculation.refundNotApplicable) && (
                  <p>${parseFloat(escrowAnalysisCalculation.intendedRefund).toFixed(2)}</p>
                )}
            </div>
          </div>

          {escrowAnalysisCalculation.intendedRefund > 0 &&
            !isStrEmptyOrNull(escrowAnalysisCalculation.refundNotApplicable) && (
              <>
                <Divider variant='middle'></Divider>
                <p className={Styles.surpluslabelrow}>
                  <p className={Styles.fontweight}>
                    {'Unable to set refund $' +
                      parseFloat(escrowAnalysisCalculation.intendedRefund).toFixed(2) +
                      ' for the following reasons: \n'}
                  </p>
                  <p className={Styles.refundnotapplicablereasons}>
                    {' '}
                    {getRefundNotApplicableStr(escrowAnalysisCalculation.refundNotApplicable)}
                  </p>
                </p>
              </>
            )}
        </div>
      }
      footer={
        <div className={Styles.buttonrow}>
          <Button fullWidth variant='contained' onClick={handleApply} className={Styles.primbutton}>
            Apply
          </Button>
          <Button
            fullWidth
            variant='contained'
            onClick={() => {
              setApplyAEAClicked(false);
            }}
            className={Styles.secondarybutton}
          >
            Cancel
          </Button>
        </div>
      }
    />
  );
};

export default confirmApplyAEAModal;
